import React, { useMemo, useState, useEffect, useCallback } from "react";
import PHOTOS from "../data/galleryPhotos";
import {
  StickerPalm, StickerSun, StickerSurf, StickerHeart, StickerFlower,
  StickerSkate, StickerStar, StickerVan, StickerWave, StickerSunflower, StickerShoe,
} from "../components/Stickers";

const YEARS = ["all", "2023", "2024", "2025", "2026"];

const Gallery = () => {
  const [year, setYear] = useState("all");
  const [zoomIdx, setZoomIdx] = useState(null);

  // Sort by date ascending (oldest first), then optionally filter by year.
  const sorted = useMemo(() => {
    return [...PHOTOS].sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  }, []);

  const filtered = useMemo(() => (
    year === "all" ? sorted : sorted.filter((p) => (p.date || "").startsWith(year))
  ), [sorted, year]);

  // Group filtered photos by year for the year-header rails.
  const grouped = useMemo(() => {
    const map = new Map();
    filtered.forEach((p) => {
      const y = (p.date || "").slice(0, 4) || "Undated";
      if (!map.has(y)) map.set(y, []);
      map.get(y).push(p);
    });
    return Array.from(map.entries()); // [ [year, [photos]], ... ]
  }, [filtered]);

  const zoom = zoomIdx !== null ? filtered[zoomIdx] : null;
  const closeZoom = useCallback(() => setZoomIdx(null), []);
  const prevZoom = useCallback(() => {
    setZoomIdx((i) => (i === null ? null : (i - 1 + filtered.length) % filtered.length));
  }, [filtered.length]);
  const nextZoom = useCallback(() => {
    setZoomIdx((i) => (i === null ? null : (i + 1) % filtered.length));
  }, [filtered.length]);

  useEffect(() => {
    if (zoomIdx === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeZoom();
      else if (e.key === "ArrowLeft") prevZoom();
      else if (e.key === "ArrowRight") nextZoom();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoomIdx, closeZoom, prevZoom, nextZoom]);

  return (
    <div>
      {/* Hero */}
      <section className="bg-[#8ECAE6] border-b-4 border-black relative overflow-hidden">
        <div className="absolute top-10 left-6 hidden md:block"><StickerPalm size={110} rotate={-12} className="float-slow" /></div>
        <div className="absolute bottom-8 right-8 hidden md:block"><StickerSurf size={110} rotate={10} className="float-med" /></div>
        <div className="max-w-7xl mx-auto py-20 px-6">
          <div className="font-pixel uppercase tracking-widest text-lg text-black/70">▒▒ photo wall ▒▒</div>
          <h1 className="font-display tracking-wider text-6xl sm:text-7xl lg:text-8xl mt-3 text-black">GALLERY</h1>
          <p className="font-body text-xl mt-5 max-w-3xl text-black/85">
            The van, the road, the runs, the talks. Oldest at the top, working down through the years.
          </p>
          <p className="font-pixel text-base mt-4 text-black/70">
            {filtered.length} {filtered.length === 1 ? "photo" : "photos"} shown
            {year !== "all" && <> · year: <strong>{year}</strong></>}
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="border-b-4 border-black bg-[#FFF4D6] py-6 px-6 sticky top-0 z-20" data-testid="gallery-filters">
        <div className="max-w-7xl mx-auto flex flex-wrap gap-3">
          {YEARS.map((y) => (
            <button
              key={y}
              onClick={() => setYear(y)}
              data-testid={`filter-${y}`}
              className={`font-display tracking-wider text-xl uppercase px-5 py-2 border-4 border-black shadow-[4px_4px_0_#000] btn-press
                ${year === y ? "bg-[#F87171] text-white" : "bg-white text-black hover:bg-[#F4D58D]"}`}
            >
              {y}
            </button>
          ))}
        </div>
      </section>

      {/* Year-grouped grids */}
      <section className="relative py-16 px-6">
        <div className="absolute top-2 left-10 hidden md:block"><StickerStar size={60} rotate={-15} className="float-slow" /></div>
        <div className="absolute top-10 right-12 hidden md:block"><StickerSun size={70} rotate={10} className="float-med" /></div>
        <div className="absolute top-1/3 left-[3%] hidden lg:block"><StickerWave size={110} rotate={8} className="float-slow" /></div>
        <div className="absolute bottom-1/3 right-[3%] hidden lg:block"><StickerShoe size={80} rotate={-12} className="float-med" /></div>
        <div className="absolute bottom-4 right-2 hidden xl:block"><StickerSunflower size={70} rotate={14} className="float-slow" /></div>

        <div className="max-w-7xl mx-auto">
          {grouped.length === 0 && (
            <div className="text-center font-pixel text-2xl text-black/60 py-20">
              No photos in this category yet.
            </div>
          )}

          {grouped.map(([year, items]) => (
            <div key={year} className="mb-16" data-testid={`year-${year}`}>
              <h2 className="font-display tracking-wider text-5xl sm:text-6xl text-black leading-none mb-8"
                  style={{ textShadow: "4px 4px 0 rgba(0,0,0,0.18)" }}>
                {year}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-10">
                {items.map((p, idx) => {
                  const rot = (idx % 5) * 1.6 - 3.2;
                  const key = `${year}-${idx}`;
                  return (
                    <button
                      key={key}
                      onClick={() => setZoomIdx(filtered.indexOf(p))}
                      data-testid={`gallery-photo-${key}`}
                      className="text-left polaroid block group"
                      style={{ transform: `rotate(${rot}deg)` }}
                    >
                      <div className="tape" style={{ top: -10, left: "50%", transform: "translateX(-50%) rotate(-2deg)" }} />
                      <div className="aspect-square overflow-hidden border-2 border-black bg-[#FFF4D6]">
                        <img src={p.src} alt={p.caption || ""} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Lightbox */}
      {zoom && (
        <div
          className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4 sm:p-6"
          onClick={closeZoom}
          data-testid="lightbox"
        >
          {/* Prev arrow */}
          <button
            onClick={(e) => { e.stopPropagation(); prevZoom(); }}
            data-testid="lightbox-prev"
            aria-label="Previous photo"
            className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 z-10 w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center bg-[#FFF4D6] text-black border-4 border-black shadow-[4px_4px_0_#000] btn-press font-display text-3xl leading-none hover:bg-[#F87171] hover:text-white transition-colors"
          >
            ‹
          </button>

          <div className="bg-white border-4 border-white p-3 max-w-[95vw] sm:max-w-3xl w-auto max-h-[95vh] flex flex-col relative" onClick={(e) => e.stopPropagation()}>
            <div className="absolute -top-3 left-4 font-pixel text-xs bg-black text-[#FFF4D6] px-2 py-0.5 border-2 border-black" data-testid="lightbox-counter">
              {zoomIdx + 1} / {filtered.length}
            </div>
            <img src={zoom.src} alt={zoom.caption || ""} className="max-h-[78vh] w-auto object-contain mx-auto" />
            <button onClick={closeZoom} data-testid="lightbox-close"
              className="mt-3 w-full font-display text-2xl tracking-wider bg-[#F87171] text-white border-4 border-black shadow-[4px_4px_0_#000] btn-press py-2">
              close
            </button>
          </div>

          {/* Next arrow */}
          <button
            onClick={(e) => { e.stopPropagation(); nextZoom(); }}
            data-testid="lightbox-next"
            aria-label="Next photo"
            className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 z-10 w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center bg-[#FFF4D6] text-black border-4 border-black shadow-[4px_4px_0_#000] btn-press font-display text-3xl leading-none hover:bg-[#F87171] hover:text-white transition-colors"
          >
            ›
          </button>
        </div>
      )}
    </div>
  );
};

export default Gallery;
